import { useQuery } from '@tanstack/react-query';
import { Table } from 'antd';
import type { TableProps } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import { ButtonPrimary } from '../../components/Button/ButtonPrimary';
import { Link } from 'react-router-dom';
import { RentPeriod } from '../../../models/RentPeriod/RentPeriod';
import { FaEdit as EditIcon, FaTrash as DeleteIcon } from 'react-icons/fa';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../components/Modals/ConfirmationModal/ConfirmationModal';

import style from './rent-period-list.module.scss';
import { useMemo, useState } from 'react';

export default function RentPeriodList() {
  type DataType = {
    key: number;
    id: number;
    duration: number;
    priceFactor: number;
    pointFactor: number;
  };

  const { data: rentPeriod = [], refetch } = useQuery(['rent-periods'], () =>
    RentPeriod.all()
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const initialValues: DataType[] = useMemo(
    () =>
      rentPeriod?.map((period) => ({
        key: period.id,
        id: period.id,
        duration: period.duration,
        priceFactor: period.priceFactor,
        pointFactor: period.pointFactor
      })) ?? [],
    [rentPeriod]
  );

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await RentPeriod.delete(deleteTarget.id);
      toast.success('Period je uspešno obrisan.');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      refetch();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Greška pri brisanju perioda.'
      );
    } finally {
      setDeleting(false);
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: 'Period [dana]',
      dataIndex: 'duration',
      sorter: (a, b) => a.duration - b.duration,
      sortOrder: sortedInfo.columnKey === 'duration' && sortedInfo.order,
      key: 'duration'
    },
    {
      title: 'Faktor Cene',
      dataIndex: 'priceFactor',
      sorter: (a, b) => a.priceFactor - b.priceFactor,
      sortOrder: sortedInfo.columnKey === 'priceFactor' && sortedInfo.order,
      key: 'priceFactor'
    },
    {
      title: 'Faktor Poena',
      dataIndex: 'pointFactor',
      sorter: (a, b) => a.pointFactor - b.pointFactor,
      sortOrder: sortedInfo.columnKey === 'pointFactor' && sortedInfo.order,
      key: 'pointFactor'
    },
    {
      title: 'Akcije',
      key: 'actions',
      render(_, row) {
        return (
          <div className="cs-table-controlls">
            <Link
              to={`/admin/rent-periods/edit/${row.id}`}
              className="cs-edit-button"
            >
              <EditIcon />
            </Link>
            <button
              className="cs-delete-button"
              onClick={() => {
                setDeleteTarget({ id: row.id });
                setShowDeleteModal(true);
              }}
            >
              <DeleteIcon />
            </button>
          </div>
        );
      }
    }
  ];

  // Table change handler for sorting
  const handleChange: TableProps<DataType>['onChange'] = (
    _pagination,
    _filters,
    sorter
  ) => {
    setSortedInfo(sorter as any);
  };

  return (
    <div className="cs-inner-container">
      <h1 className="cs-title">Lista Perioda Izdavanja</h1>
      <div className={style.info}>
        <div className={style.number}>
          Trenutno imate <span>{rentPeriod?.length}</span> perioda
        </div>
        <ButtonPrimary url="/admin/rent-periods/create">
          Dodaj Period
        </ButtonPrimary>
      </div>
      <Table
        columns={columns}
        dataSource={initialValues}
        className="cs-table-wrapper"
        rowKey={'id'}
        onChange={handleChange}
      />
      <ConfirmationModal
        show={showDeleteModal}
        title="Potvrda brisanja"
        message={
          deleteTarget
            ? `Da li ste sigurni da želite da obrišete period izdavanja sa ID "${deleteTarget.id}"?`
            : ''
        }
        confirmLabel={deleting ? 'Brišem...' : 'Obriši'}
        cancelLabel="Otkaži"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
}
